import { TranslateFn } from 'src/localization/translate'

// Types mirror the extend-api card-feeds DTOs (CardFeedResponse,
// CardFeedEntryResponse). See organizations-card-feeds/libs/schema in
// extend-api. NOTE: the card-feeds API returns its own pagination shape
// ({ page, pageSize, totalPages }) which differs from the app-wide
// PaginationResponse — keep this local type, do not swap in the shared one.

export type CardNetwork = 'VISA' | 'MASTERCARD' | 'AMERICAN_EXPRESS'

// Mirrors the extend-api CardFeedStatus enum (organizations-card-feeds:
// commands/CardFeed.kt). Wire-aligned with the integrator (Astrada) lifecycle:
// SUBMITTED (feed created, awaiting activation) → ACTIVE (connected) and the
// FAILED / DEACTIVATED terminal states.
export type CardFeedStatus = 'SUBMITTED' | 'ACTIVE' | 'FAILED' | 'DEACTIVATED'

export type CardFeedCardStatus =
  | 'ASSIGNED'
  | 'UNASSIGNED'
  | 'PENDING'
  | 'FAILED'

// UI filter values for the card-feed table. The API only returns one PENDING
// status; PENDING_INVITE_SENT is derived from PENDING + an invited assignee so
// users can filter invite-sent rows separately from other pending conversions.
export type CardFeedCardStatusFilter =
  | CardFeedCardStatus
  | 'PENDING_INVITE_SENT'

export interface CardFeedPagination {
  page: number
  pageSize: number
  totalPages: number
}

export interface CardFeedResponse {
  id: string
  organizationId: string
  integratorFeedId: string
  status: CardFeedStatus
  cardNetwork: CardNetwork
  // Null until the feed's BIN range is learned.
  cardProgramName: string | null
  // The API always returns a count and an obfuscated reference id, but they're
  // typed leniently here — the feeds list/header already null-guard them.
  cardCount?: number | null
  referenceId?: string | null
  createdAt: string
  // NOTE: the API intentionally does not return updatedAt for card feeds.
}

export interface CardFeedsResponse {
  cardFeeds: CardFeedResponse[]
  pagination: CardFeedPagination
}

// Avatar source the API resolved for the assigned user — mirrors the extend-api
// UserAvatarType enum (the app-wide UserAvatarType enum omits LINKEDIN, so keep
// a local string union rather than coupling to it).
export type CardFeedAvatarType = 'LINKEDIN' | 'UPLOAD' | 'GRAVATAR' | 'DEFAULT'

// The assignee's standing in the org, resolved by the API:
//  - ACTIVE      — current member.
//  - INVITED     — has a pending org invite, not accepted yet.
//  - DEACTIVATED — removed member.
export type CardFeedMembershipStatus = 'ACTIVE' | 'INVITED' | 'DEACTIVATED'

// Mirrors the extend-api CardFeedAssignedUserResponse DTO — the resolved
// assignee for a card (omitted until a user is assigned and can be resolved;
// assignedUserId still carries the linkage in that case). The card-feed table
// links the name to the user's detail page for every membership status (an
// INVITED user resolves to their invitee page); membershipStatus only drives
// the deactivated avatar treatment.
export interface CardFeedAssignedUser {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  avatarType: CardFeedAvatarType
  avatarUrl?: string | null
  organizationId: string
  organizationRole?: string | null
  membershipStatus: CardFeedMembershipStatus
}

// The minimal user shape UserAvatarCell renders. Derived from the assigned user
// so the card-feed table shows the same avatar view as the assignment flows.
export interface CardFeedAvatarUser {
  id: string
  fullNameOrEmail: string
  initials: string
  avatarUrl?: string
  deactivated?: boolean
  truncatedFullNameOrEmail?: string
  hasFullName: boolean
}

// Integrator-reported physical-card status (distinct from the assignment
// `status`). Mirrors the extend-api CardFeedEntryPhysicalCardStatus enum.
export type CardFeedPhysicalCardStatus =
  | 'ACTIVE'
  | 'PENDING'
  | 'PENDING_CONFIRMATION'
  | 'INACTIVE'
  | 'EXPIRED'
  | 'BLOCKED'
  | 'CLOSED'
  | 'NOT_SUPPORTED'
  | 'ENROLLMENT_ERROR'
  | 'UNKNOWN'

export interface CardFeedEntryResponse {
  id: string
  displayName: string
  last4: string
  // Omitted by the API until a user is assigned.
  assignedUserId?: string | null
  // Omitted until a user is assigned or the assignee can't be resolved.
  assignedUser?: CardFeedAssignedUser | null
  status: CardFeedCardStatus
  // Omitted for entries created before the imported-card messaging flow.
  physicalCardStatus?: CardFeedPhysicalCardStatus | null
  createdAt: string
  // NOTE: the API intentionally does not return updatedAt for card feeds.
}

export interface CardFeedEntriesResponse {
  creditCards: CardFeedEntryResponse[]
  pagination: CardFeedPagination
}

// View model consumed by CardFeedCardsTable. Maps API field names to the names
// the table renders. assignedUser is the resolved account owner; the owner
// display string used for search + sort is derived from it on demand via
// assignedUserDisplayName (there's no separate stored display field).
export interface CardFeedCard {
  id: string
  cardName: string
  lastFour: string
  assignedUser: CardFeedAssignedUser | null
  status: CardFeedCardStatus
  dateAdded: string
}

export const isInviteSentCardFeedCard = (
  card: Pick<CardFeedCard, 'status' | 'assignedUser'>,
): boolean =>
  card.status === 'PENDING' &&
  card.assignedUser?.membershipStatus === 'INVITED'

// The header and the top-bar breadcrumb must show the same feed name. A
// submitted (pending) feed has no connected card program yet, so we may not
// have a real name to show — fall back to a generic "Pending Feed" label;
// otherwise use the program name, or a plain "Card Feed" while it's unknown.
export const getCardFeedDisplayName = (
  feed: Pick<CardFeedResponse, 'status' | 'cardProgramName'> | undefined,
  translate: TranslateFn,
): string =>
  feed?.status === 'SUBMITTED' || feed?.status === 'FAILED'
    ? translate('Pending Feed')
    : (feed?.cardProgramName ?? translate('Card Feed'))

const fullNameOf = (user: CardFeedAssignedUser): string | undefined => {
  const fullName = [user.firstName, user.lastName]
    .filter((part) => !!part)
    .join(' ')
    .trim()
  return fullName || undefined
}

export const assignedUserDisplayName = (
  user: CardFeedAssignedUser | null | undefined,
): string | null => {
  if (!user) return null
  return fullNameOf(user) ?? user.email ?? null
}

// Mirrors UserListModel.truncatedFullNameOrEmail so the avatar cell truncates
// long names the same way the rest of the app does.
const truncateName = (value: string): string =>
  value.length > 45 ? `${value.slice(0, 44)}...` : value

// Maps the assigned user into the structural shape UserAvatarCell expects,
// deriving initials and name the same way UserListModel does.
export const toCardFeedAvatarUser = (
  user: CardFeedAssignedUser,
): CardFeedAvatarUser => {
  const fullName = fullNameOf(user)
  const fullNameOrEmail = fullName ?? user.email ?? ''
  // Mirror UserListModel.initials exactly: full-name initials are uppercased,
  // but the single-character email fallback is taken as-is (no uppercasing), so
  // name-less assignees (e.g. invitees) render the same initial as elsewhere.
  const initials =
    user.firstName && user.lastName
      ? `${user.firstName.slice(0, 1)}${user.lastName.slice(0, 1)}`.toUpperCase()
      : (user.email?.[0] ?? '')
  return {
    id: user.id,
    fullNameOrEmail,
    truncatedFullNameOrEmail: truncateName(fullNameOrEmail),
    initials,
    avatarUrl: user.avatarUrl ?? undefined,
    deactivated: user.membershipStatus === 'DEACTIVATED',
    hasFullName: !!fullName,
  }
}

export const toCardFeedCard = (entry: CardFeedEntryResponse): CardFeedCard => ({
  id: entry.id,
  cardName: entry.displayName,
  lastFour: entry.last4,
  assignedUser: entry.assignedUser ?? null,
  status: entry.status,
  dateAdded: entry.createdAt,
})

// Mirrors AssignCardFeedCardsRequest / AssignCardFeedCardsResponse in
// extend-api (organizations-card-feeds/libs/schema). The endpoint resolves the
// assignee from the email — self, existing member (promoted to CARD_MANAGER),
// or a brand-new invite — and grants Card Manager access to the listed cards.
export interface AssignCardFeedCardsRequest {
  email: string
  creditCardIds: string[]
}

export interface AssignCardFeedCardsResponse {
  message: string
  assignedCount: number
}

// Mirrors UnassignCardFeedCardResponse in extend-api (organizations-card-feeds/
// libs/schema). POST /cardfeeds/{cardFeedId}/cards/{creditCardId}/unassign.
// Idempotent: unassigning a card with no assignee returns unassignedCount: 0.
export interface UnassignCardFeedCardResponse {
  message: string
  unassignedCount: number
}

// Mirrors UpdateCardFeedRequest in extend-api (organizations-card-feeds/libs/
// schema). The PATCH endpoint only updates the user-visible program name; the
// backend rejects a blank name (@NotBlank) and caps it at 200 chars (@Size).
export interface UpdateCardFeedRequest {
  cardProgramName: string
}

export type CreateCardFeedRequest =
  | {
      cardNetwork: 'MASTERCARD'
      deliveryId: string
    }
  | {
      cardNetwork: 'VISA'
      regionId: string
      processorId: string
      issuerId: string
      companyId: string
    }
